/**
 * This service should handle scheduling of api calls that update line items in the cart.
 *
 * The rules are as follows:
 * - There can be up to six update and add calls in parallel
 * - If a delete call is scheduled, it needs to wait for all calls currently in progress to finish
 * - If a delete call is scheduled, all new calls need to wait for the delete call to finish
 * - Delete calls are run before update and add calls
 */

const MAX_PARALLEL_CALLS = 6;

type CallTypes = "add" | "update" | "delete" | "deleteCart" | "cartView" | "miniCart";

export interface CallProcessorDefinition<TData, TResult = Response> {
  type: CallTypes;
  execute: (data: TData) => Promise<TResult>;
  canBeExecuted: (ongoingCalls: QueuedCall[]) => boolean;
}

type AnyCallProcessorDefinition = CallProcessorDefinition<unknown, unknown>;

const callProcessors: Partial<Record<CallTypes, AnyCallProcessorDefinition>> = {};

export function registerCallProcessor<TData, TResult = Response>(definition: CallProcessorDefinition<TData, TResult>) {
  callProcessors[definition.type] = definition as AnyCallProcessorDefinition;
}

export function scheduleCall<TResult = Response>(type: CallTypes, data: unknown): Promise<TResult> {
  return new Promise((resolve, reject) => {
    const call: QueuedCall = {
      type,
      data,
      resolve: (result) => resolve(result as TResult),
      reject,
    };
    callQueue.push(call);
    processQueue();
  });
}

export type QueuedCall = {
  type: CallTypes;
  data: unknown;
  resolve: (data: unknown) => void;
  reject: (error: unknown) => void;
};

const callQueue: QueuedCall[] = [];
const ongoingCallsCartService: QueuedCall[] = [];

function processQueue() {
  while (ongoingCallsCartService.length < MAX_PARALLEL_CALLS && callQueue.length > 0) {
    const nextCall = callQueue[0]!;

    const processor = callProcessors[nextCall.type];
    if (!processor) {
      console.error("No processor found for call type", nextCall.type);
      callQueue.shift();
      continue;
    }

    if (processor.canBeExecuted(ongoingCallsCartService)) {
      execute(nextCall);
      callQueue.shift();
    }

    if (nextCall === callQueue[0]) {
      return;
    }
  }
}

function execute(call: QueuedCall) {
  ongoingCallsCartService.push(call);

  const processor = callProcessors[call.type];

  if (!processor) {
    call.reject(new Error(`No processor found for call type ${call.type}`));
    ongoingCallsCartService.splice(ongoingCallsCartService.indexOf(call), 1);
    processQueue();
    return;
  }

  processor
    .execute(call.data)
    .then((data) => {
      call.resolve(data);
    })
    .catch((error) => {
      call.reject(error);
    })
    .finally(() => {
      ongoingCallsCartService.splice(ongoingCallsCartService.indexOf(call), 1);
      processQueue();
    });
}
